<template>
    <div class="relative" data-testid="select-container" ref="wrapperRef" v-click-away="close">
        <activix-popover
            :class-name="popoverClassName"
            :popover-style="popoverStyle"
            data-testid="select-popover"
            event="manual"
            placement="bottom-start"
            :same-width="sameWidth"
            ref="popperRef"
            @enter="onOpen"
            @keydown.down.prevent.stop="onKeyDown"
            @keydown.enter.prevent.stop="onKeyEnter"
            @keydown.esc.stop.prevent="onKeyEscape"
            @keydown.tab.stop.prevent="onKeyTab"
        >
            <template #trigger>
                <div
                    data-testid="select-trigger-container"
                    ref="triggerRef"
                    @click="toggle"
                    @mouseenter="onMouseEnter"
                >
                    <div class="flex items-center">
                        <slot name="prefix" />
                        <slot
                            :bold-highlighted="boldHighlighted"
                            :disabled="disabled"
                            :highlighted="highlighted"
                            :invisible="invisible"
                            name="trigger"
                            :opened="show"
                            :padding-class-name="triggerPaddingClassName"
                            :placeholder="placeholder"
                            :selected="multiple ? selectedOptions : selectedOptions[0] || null"
                        >
                            <select-trigger
                                :aria-describedby="ariaDescribedby"
                                :aria-label="ariaLabel"
                                :bold-highlighted="boldHighlighted"
                                :color="effectiveColor"
                                :clearable="clearable"
                                :disabled="disabled"
                                :has-error="hasError"
                                :highlighted="highlighted"
                                :invisible="invisible"
                                :limit-selected-visible="limitSelectedVisible"
                                :limit-text="limitText"
                                :loading="loading"
                                :opened="show"
                                :padding-class-name="triggerPaddingClassName"
                            >
                                <template #triggerIcon>
                                    <slot name="triggerIcon" />
                                </template>
                            </select-trigger>
                        </slot>
                    </div>
                </div>
            </template>

            <template v-if="filterable">
                <select-search
                    class="border-b border-gray-200"
                    data-testid="select-search"
                    :value="search"
                    ref="searchRef"
                    @enter="onKeyEnter"
                    @input="search = $event"
                />

                <div
                    class="italic py-2 text-center text-gray-600"
                    data-testid="select-no-results"
                    v-text="messages.noResults"
                    v-if="!hasFilteredOptions"
                />
            </template>
            <template v-else-if="!hasOptions && !slots.default">
                <div
                    class="italic py-2 text-center text-gray-600"
                    data-testid="select-no-options"
                    v-text="messages.noOption"
                />
            </template>

            <div v-if="shouldRenderOptions">
                <select-options
                    data-testid="select-options-container"
                    ref="optionListRef"
                    v-show="show && (hasFilteredOptions || slots.default)"
                >
                    <template v-if="hasSelectAll">
                        <select-all
                            :all-selected="isAllSelected"
                            class="border-b border-gray-200"
                            data-testid="select-all"
                            :partially-selected="isPartiallySelected"
                        />
                    </template>
                    <slot :options="filteredOptions">
                        <template v-if="isGrouped">
                            <select-option-group
                                :label="group.label"
                                :key="index"
                                v-for="(group, index) in normalizedGroupedOptions"
                            >
                                <select-option
                                    :bold="option.bold"
                                    data-testid="select-group-option"
                                    :disabled="option.disabled"
                                    :divided="option.divided"
                                    :icon="option.icon"
                                    :icon-style="option.iconStyle"
                                    :label="option.label"
                                    :suffix-icon="option.suffixIcon"
                                    :tooltip="option.tooltip"
                                    :value="option.value"
                                    :key="option.value"
                                    ref="optionsRef"
                                    v-for="option in group.options"
                                />
                            </select-option-group>
                        </template>
                        <template v-else>
                            <select-option
                                :bold="option.bold"
                                :disabled="option.disabled"
                                :divided="option.divided"
                                :icon="option.icon"
                                :icon-style="option.iconStyle"
                                :label="option.label"
                                :suffix-icon="option.suffixIcon"
                                :tooltip="option.tooltip"
                                :value="option.value"
                                :key="option.value"
                                ref="optionsRef"
                                v-for="option in normalizedFlatOptions"
                            />
                        </template>
                    </slot>
                </select-options>
            </div>
        </activix-popover>
    </div>
</template>

<script lang="ts" setup>
    import { computed, inject, onMounted, provide, reactive, ref, useSlots, watch } from 'vue';
    import ActivixPopover from '@/components/activix-ui/ActivixPopover/ActivixPopover.vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import SelectOption from '@/components/activix-ui/ActivixSelect/ActivixSelectOption.vue';
    import SelectOptions from '@/components/activix-ui/ActivixSelect/ActivixSelectOptions.vue';
    import SelectTrigger from '@/components/activix-ui/ActivixSelect/ActivixSelectTrigger.vue';
    import SelectOptionGroup from '@/components/activix-ui/ActivixSelect/ActivixSelectOptionGroup.vue';
    import SelectSearch from '@/components/activix-ui/ActivixSelect/ActivixSelectSearch.vue';
    import SelectAll from '@/components/activix-ui/ActivixSelect/ActivixSelectAll.vue';
    import { debugLog } from '@/utils/debug.ts';
    import type {
        SelectOption as SelectOptionType,
        SelectOptionGroup as SelectOptionGroupType,
        SelectState,
        SelectEvents,
        SelectRefs,
        FilterMethod,
        SelectOptionValue,
        CustomLabelFunction,
        PrimitiveOption,
        SelectColor,
        TransmissionType,
        SelectedPropValue,
    } from '@/components/activix-ui/ActivixSelect/types.ts';

    defineOptions({ compatConfig: { MODE: 3 } });

    const SELECT_ALL_VALUE = 'selectAll';

    const { t } = useI18n();
    const slots = useSlots();
    const oldFontScale = inject<boolean>('oldFontScale', true);

    type ValueType = string | string[] | number | symbol;
    type OptionInput = SelectOptionType | SelectOptionGroupType | PrimitiveOption;

    function isOptionGroup(option: OptionInput): option is SelectOptionGroupType {
        return (
            typeof option === 'object' &&
            option !== null &&
            'options' in option &&
            Array.isArray(option.options)
        );
    }

    function isPrimitiveOption(option: OptionInput): option is PrimitiveOption {
        return typeof option === 'string' || typeof option === 'number';
    }

    function normalizePrimitiveOption(option: PrimitiveOption, index: number): SelectOptionType {
        return {
            value: index,
            label: String(option),
        };
    }

    function getOptionValue(option: SelectOptionType, key: string): SelectOptionValue {
        const value = option[key as keyof SelectOptionType];
        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'symbol'
        ) {
            return value;
        }
        return null;
    }

    function getOptionLabel(option: SelectOptionType, key: string): string {
        if (props.customLabel) {
            return props.customLabel(option, key);
        }
        const value = option[key as keyof SelectOptionType];
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }

        return '';
    }

    function isOptionDisabled(option: SelectOptionType): boolean {
        const value = option.disabled;
        return typeof value === 'boolean' ? value : false;
    }

    const props = withDefaults(
        defineProps<{
            ariaDescribedby?: string;
            ariaLabel?: string;
            automaticDropdown?: boolean;
            automaticFocus?: boolean;
            automaticRender?: boolean;
            boldHighlighted?: boolean;
            clearable?: boolean;
            closeOnSelect?: boolean;
            color?: SelectColor;
            customLabel?: CustomLabelFunction;
            disabled?: boolean;
            filterMethod?: FilterMethod;
            filterable?: boolean;
            focusOnEnter?: boolean;
            hasError?: boolean;
            heightClassName?: string;
            highlighted?: boolean;
            identifierKey?: string;
            invisible?: boolean;
            labelKey?: string;
            limit?: number;
            limitSelectedVisible?: number;
            limitText?: (count: number) => string;
            loading?: boolean;
            multiple?: boolean;
            noneLabel?: string;
            noneSelected?: boolean;
            noOptionMessage?: string;
            noResultsMessage?: string;
            outline?: boolean;
            nullable?: boolean;
            nullableOptionLabel?: string;
            options?: OptionInput[];
            popoverClassName?: string;
            popoverStyle?: Record<string, string | number>;
            placeholder?: string;
            resetAfter?: boolean;
            sameWidth?: boolean;
            selectAll?: boolean;
            selected?: SelectedPropValue;
            taggable?: boolean;
            transparent?: boolean;
            triggerPaddingClassName?: string | unknown[];
            modelValue?: ValueType;
            // FIXME(VUE3): Remove 'value' prop once all consumers use v-model (modelValue) and @vue/compat is removed
            value?: ValueType;
            valueKey?: string;
            widthClassName?: string;
            withCount?: boolean;
            withNone?: boolean;
        }>(),
        {
            ariaDescribedby: undefined,
            ariaLabel: undefined,
            automaticDropdown: false,
            automaticFocus: false,
            automaticRender: false,
            clearable: false,
            closeOnSelect: undefined,
            color: undefined,
            disabled: false,
            filterable: false,
            focusOnEnter: true,
            hasError: false,
            heightClassName: 'max-h-60',
            highlighted: false,
            boldHighlighted: false,
            identifierKey: undefined,
            invisible: false,
            labelKey: 'label',
            limit: 99999,
            loading: false,
            multiple: false,
            noneLabel: '',
            noneSelected: false,
            noOptionMessage: '',
            noResultsMessage: '',
            outline: false,
            nullable: false,
            nullableOptionLabel: '---',
            options: () => [],
            popoverClassName: '',
            popoverStyle: undefined,
            placeholder: '---',
            resetAfter: false,
            sameWidth: true,
            selectAll: false,
            selected: undefined,
            taggable: false,
            transparent: false,
            triggerPaddingClassName: 'py-2 px-2.5',
            valueKey: 'value',
            widthClassName: 'min-w-48',
            withCount: true,
            withNone: false,
        }
    );

    // FIXME(VUE3): Remove 'input' event once all consumers use v-model (update:modelValue) and @vue/compat is removed
    const emit = defineEmits<{
        (event: 'input', value: ValueType | null): void;
        (event: 'update:modelValue', value: ValueType | null): void;
        (event: 'update', value: OptionInput | OptionInput[] | string | string[] | null): void;
        (event: 'open'): void;
        (event: 'close'): void;
        (event: 'blur'): void;
    }>();

    const emitValue = (value: ValueType | null) => {
        emit('input', value);
        emit('update:modelValue', value);
    };

    const wrapperRef = ref<HTMLElement | null>(null);
    const triggerRef = ref<HTMLElement | null>(null);
    const popperRef = ref<InstanceType<typeof ActivixPopover> | null>(null);
    const searchRef = ref<InstanceType<typeof SelectSearch> | null>(null);
    const optionListRef = ref<InstanceType<typeof SelectOptions> | null>(null);
    const optionsRef = ref<InstanceType<typeof SelectOption>[]>([]);

    const show = ref(false);
    const initiallyOpened = ref(false);
    const hasHovered = ref(false);
    const search = ref('');

    // Transformation logic for selected prop support
    const transmissionType = computed<TransmissionType>(() => {
        if (!props.options || props.options.length === 0) {
            return 'object';
        }
        const firstOption = props.options[0];
        return isPrimitiveOption(firstOption) ? 'string' : 'object';
    });

    const effectiveIdentifierKey = computed(() => props.identifierKey || props.valueKey);

    const effectiveColor = computed<SelectColor>(() => {
        if (props.color) {
            return props.color;
        }

        if (props.outline) {
            return 'outline';
        }

        if (props.transparent) {
            return 'transparent';
        }

        return 'white';
    });

    // Keep a string sentinel for legacy compatibility.
    // Do not use Symbol here: many existing consumers explicitly check for "none"
    // and expect a serializable value in emitted payloads.
    const NONE_VALUE = 'none';

    function buildNoneOption(): SelectOptionType {
        const noneOption: SelectOptionType = {
            label: props.noneLabel || t('general.none'),
            value: NONE_VALUE,
        };

        if (props.labelKey !== 'label') {
            noneOption[props.labelKey] = props.noneLabel || t('general.none');
        }

        if (props.valueKey !== 'value') {
            noneOption[props.valueKey] = NONE_VALUE;
        }

        return noneOption;
    }

    // Normalize user-created tags into option objects so taggable selections
    // remain renderable even when parents round-trip values without mutating options.
    function buildTagOption(tagValue: string): SelectOptionType {
        const tagOption: SelectOptionType = {
            label: tagValue,
            value: tagValue,
        };

        if (props.labelKey !== 'label') {
            tagOption[props.labelKey] = tagValue;
        }

        if (props.valueKey !== 'value') {
            tagOption[props.valueKey] = tagValue;
        }

        if (
            props.identifierKey &&
            props.identifierKey !== props.valueKey &&
            props.identifierKey !== 'value'
        ) {
            tagOption[props.identifierKey] = tagValue;
        }

        return tagOption;
    }

    const isGrouped = computed(() => {
        return (
            props.options.length > 0 &&
            props.options.every((option) => !isPrimitiveOption(option) && isOptionGroup(option))
        );
    });

    const state = reactive<SelectState>({
        selectedOptions: [],
        get isGrouped() {
            return isGrouped.value;
        },
        isTaggable: props.multiple && props.taggable,
        multiple: props.multiple,
        placeholder: props.placeholder,
        selectAllValue: SELECT_ALL_VALUE,
        withCount: props.withCount,
    });

    const formattedValue = computed(() => {
        if (props.multiple) {
            return effectiveValue.value;
        }

        if (effectiveValue.value === '') {
            return null;
        }

        return effectiveValue.value ?? null;
    });

    const groupedOptions = computed((): SelectOptionGroupType[] | null => {
        if (!isGrouped.value) {
            return null;
        }
        return props.options.filter(isOptionGroup) as SelectOptionGroupType[];
    });

    const flatOptions = computed((): SelectOptionType[] | null => {
        if (isGrouped.value) {
            return null;
        }
        return props.options
            .map((opt, index) => {
                if (isPrimitiveOption(opt)) {
                    return normalizePrimitiveOption(opt, index);
                }
                if (isOptionGroup(opt)) {
                    return null;
                }
                return opt;
            })
            .filter((opt): opt is SelectOptionType => opt !== null);
    });

    // Map to preserve original option references for selected prop support
    const originalOptionsMap = computed(() => {
        const map = new Map<SelectOptionValue, OptionInput>();
        if (!props.options) return map;

        for (const [index, option] of props.options.entries()) {
            if (isPrimitiveOption(option)) {
                map.set(index, option);
            } else if (isOptionGroup(option)) {
                for (const groupOption of option.options) {
                    const value = getOptionValue(groupOption, effectiveIdentifierKey.value);
                    map.set(value, groupOption);
                }
            } else {
                const value = getOptionValue(option, effectiveIdentifierKey.value);
                map.set(value, option);
            }
        }

        if (props.withNone) {
            map.set(NONE_VALUE, buildNoneOption());
        }

        return map;
    });

    /**
     * Finds the original option by its value key.
     * Handles type coercion between string and number keys since Map uses strict equality.
     */
    function findOriginalOption(value: SelectOptionValue): OptionInput | undefined {
        const map = originalOptionsMap.value;

        // Try direct lookup first
        if (map.has(value)) {
            return map.get(value);
        }

        // Handle string/number coercion: "1" vs 1
        if (typeof value === 'string') {
            const numericValue = Number(value);
            if (!Number.isNaN(numericValue) && map.has(numericValue)) {
                return map.get(numericValue);
            }
        } else if (typeof value === 'number') {
            const stringValue = String(value);
            if (map.has(stringValue)) {
                return map.get(stringValue);
            }
        }

        return undefined;
    }

    function resolveMultipleSelection(values: string[]): Array<OptionInput | string> {
        if (transmissionType.value === 'object') {
            return values
                .map((v) => {
                    const original = findOriginalOption(v);

                    if (original) {
                        return original;
                    }

                    if (!props.taggable) {
                        return undefined;
                    }

                    return buildTagOption(v);
                })
                .filter((opt): opt is SelectOptionType => opt !== undefined);
        }

        return values
            .map((v) => findOriginalOption(v) ?? (props.taggable ? v : undefined))
            .filter((opt): opt is PrimitiveOption | string => opt !== undefined);
    }

    function resolveSingleSelection(value: SelectOptionValue): OptionInput | null {
        if (value === null || value === undefined) {
            return null;
        }

        if (transmissionType.value === 'object') {
            return (findOriginalOption(value) as SelectOptionType) || null;
        }

        return (findOriginalOption(value) as PrimitiveOption) || null;
    }

    function findPrimitiveFlatOption(selected: PrimitiveOption): SelectOptionType | undefined {
        const flat = flatOptions.value || [];
        const selectedAsString = String(selected);

        // Match raw label first (legacy primitive behavior), then by value key
        // and finally by custom label output.
        return flat.find((option) => {
            const rawLabelValue = option[props.labelKey as keyof SelectOptionType];
            const optionValue = getOptionValue(option, props.valueKey);

            return (
                (typeof rawLabelValue === 'string' && rawLabelValue === selectedAsString) ||
                (optionValue !== null && String(optionValue) === selectedAsString) ||
                getOptionLabel(option, props.labelKey) === selectedAsString
            );
        });
    }

    function appendNoneSelection(values: SelectOptionValue[]): SelectOptionValue[] {
        if (!props.withNone || !props.noneSelected) {
            return values;
        }

        if (values.includes(NONE_VALUE)) {
            return values;
        }

        return [NONE_VALUE, ...values];
    }

    // Convert selected prop (objects/primitives) to internal value keys
    const selectedToInternalValue = computed<ValueType | null>(() => {
        if (props.selected === undefined) {
            return undefined as unknown as ValueType | null;
        }

        if (props.multiple) {
            if (!Array.isArray(props.selected) || props.selected.length === 0) {
                return appendNoneSelection([]) as unknown as string[];
            }

            return appendNoneSelection(
                props.selected.map((item) => {
                    if (isPrimitiveOption(item)) {
                        const found = findPrimitiveFlatOption(item);
                        return found ? getOptionValue(found, props.valueKey) : item;
                    }

                    return getOptionValue(item as SelectOptionType, effectiveIdentifierKey.value);
                })
            ) as unknown as string[];
        }

        if (props.selected === null) {
            // Handle noneSelected for null
            if (props.withNone && props.noneSelected) {
                return NONE_VALUE;
            }

            return null;
        }

        if (props.withNone && props.noneSelected) {
            return NONE_VALUE;
        }

        if (isPrimitiveOption(props.selected)) {
            const found = findPrimitiveFlatOption(props.selected);
            return found ? (getOptionValue(found, props.valueKey) as string) : null;
        }

        return getOptionValue(
            props.selected as SelectOptionType,
            effectiveIdentifierKey.value
        ) as string;
    });

    // Use selected-derived value if provided, otherwise fall back to value prop
    // FIXME(VUE3): Remove props.value fallback once all consumers use v-model and @vue/compat is removed
    const effectiveValue = computed<ValueType | null>(() => {
        if (props.selected !== undefined) {
            return selectedToInternalValue.value;
        }
        return props.modelValue ?? props.value ?? null;
    });

    const internalOptions = computed(() => {
        if (isGrouped.value) {
            return [];
        }

        const flat = flatOptions.value;
        if (!flat) {
            return [];
        }

        const options = [...flat];
        const emptyOptionExists = options.some((option) => {
            return (
                option[props.valueKey as keyof SelectOptionType] === null ||
                option[props.valueKey as keyof SelectOptionType] === ''
            );
        });

        if (props.nullable && !props.multiple && !emptyOptionExists) {
            const nullOption: SelectOptionType = {
                label: props.nullableOptionLabel,
                value: null,
            };
            if (props.labelKey !== 'label') {
                nullOption[props.labelKey] = props.nullableOptionLabel;
            }
            if (props.valueKey !== 'value') {
                nullOption[props.valueKey] = null;
            }
            options.unshift(nullOption);
        }

        if (props.withNone) {
            options.unshift(buildNoneOption());
        }

        return options;
    });

    const flattenedOptions = computed(() => {
        if (!isGrouped.value) {
            return internalOptions.value;
        }

        const groups = groupedOptions.value;
        if (!groups) {
            return [];
        }

        return groups.reduce((options: SelectOptionType[], group) => {
            return options.concat(group.options);
        }, []);
    });

    const internalFilterMethod = (option: SelectOptionType, searchValue: string) => {
        if (!searchValue) {
            return true;
        }

        const label = getOptionLabel(option, props.labelKey);

        return label.toLowerCase().includes(searchValue.toLowerCase());
    };

    const filteredGroupedOptions = computed((): SelectOptionGroupType[] => {
        const method = props.filterMethod || internalFilterMethod;
        const groups = groupedOptions.value;
        if (!groups) {
            return [];
        }

        return groups
            .map((option) => ({
                ...option,
                options: option.options.filter((o) => method(o, search.value)),
            }))
            .filter((group) => group.options.length);
    });

    const filteredFlatOptions = computed((): SelectOptionType[] => {
        const method = props.filterMethod || internalFilterMethod;
        return internalOptions.value.filter((option) =>
            method(option as SelectOptionType, search.value)
        );
    });

    type NormalizedOption = {
        bold: boolean;
        disabled: boolean;
        divided: boolean;
        icon: string | undefined;
        iconStyle: string | undefined;
        label: string;
        suffixIcon: string | undefined;
        tooltip: string | undefined;
        value: SelectOptionValue;
    };

    const isLimitReached = computed(() => {
        if (!props.multiple) {
            return false;
        }

        const currentValues = (formattedValue.value as string[]) || [];
        return currentValues.length >= props.limit;
    });

    const isOptionDisabledByLimit = (option: SelectOptionType): boolean => {
        if (!isLimitReached.value) {
            return false;
        }

        const optionValue = getOptionValue(option, props.valueKey);
        const currentValues = (formattedValue.value as string[]) || [];
        return !currentValues.includes(optionValue as string);
    };

    const normalizeOption = (option: SelectOptionType): NormalizedOption => ({
        bold: !!option.bold,
        disabled: isOptionDisabled(option) || isOptionDisabledByLimit(option),
        divided: !!option.divided,
        icon: option.icon,
        iconStyle: option.iconStyle,
        label: getOptionLabel(option, props.labelKey),
        suffixIcon: option.suffixIcon,
        tooltip: option.tooltip,
        value: getOptionValue(option, props.valueKey),
    });

    const normalizedGroupedOptions = computed(() => {
        return filteredGroupedOptions.value.map((group) => ({
            ...group,
            options: group.options.map(normalizeOption),
        }));
    });

    const normalizedFlatOptions = computed((): NormalizedOption[] => {
        return filteredFlatOptions.value.map(normalizeOption);
    });

    const filteredOptions = computed(() => {
        if (isGrouped.value) {
            return filteredGroupedOptions.value;
        }
        return filteredFlatOptions.value;
    });

    const hasOptions = computed(() => {
        return flattenedOptions.value.length > 0;
    });

    const hasFilteredOptions = computed(() => {
        if (isGrouped.value) {
            return filteredGroupedOptions.value.length > 0;
        }

        return filteredFlatOptions.value.length > 0;
    });

    const selectedOptions = computed(() => {
        if (props.multiple) {
            const selectedValues = (formattedValue.value as string[]) || [];

            return selectedValues
                .map((selectedValue) => {
                    const existingOption = flattenedOptions.value.find(
                        (option) => getOptionValue(option, props.valueKey) === selectedValue
                    );

                    if (existingOption) {
                        return existingOption;
                    }

                    if (!props.taggable) {
                        return undefined;
                    }

                    return buildTagOption(selectedValue);
                })
                .filter((option): option is SelectOptionType => option !== undefined);
        }

        return flattenedOptions.value.filter(
            (option) => option[props.valueKey as keyof SelectOptionType] === formattedValue.value
        );
    });

    const messages = computed(() => ({
        noResults: props.noResultsMessage || t('clientCard.select.noResults'),
        noOption: props.noOptionMessage || t('clientCard.select.noOption'),
    }));

    const popoverClassName = computed(() => {
        const className = [
            props.heightClassName,
            props.widthClassName,
            'text-base bg-white rounded-md shadow-lg border border-gray-200 overflow-auto',
        ];

        if (props.popoverClassName) {
            className.push(props.popoverClassName);
        }

        if (!oldFontScale) {
            className.push('sm:text-sm');
        }

        return className.join(' ');
    });

    const allValues = computed(() => {
        return flattenedOptions.value.map(
            (option) => option[props.valueKey as keyof SelectOptionType]
        );
    });

    const selectedValues = computed(() => {
        return selectedOptions.value.map(
            (option) => option[props.valueKey as keyof SelectOptionType]
        );
    });

    const isAllSelected = computed(() => {
        return allValues.value.every((value) => selectedValues.value.includes(value));
    });

    const isPartiallySelected = computed(() => {
        return allValues.value.some((value) => selectedValues.value.includes(value));
    });

    const hasSelectAll = computed(() => {
        return props.multiple && props.selectAll;
    });

    const shouldRenderOptions = computed(() => {
        return props.automaticRender || hasHovered.value || initiallyOpened.value;
    });

    const onMouseEnter = () => {
        hasHovered.value = true;
    };

    watch(
        selectedOptions,
        (newSelectedOptions) => {
            state.selectedOptions = newSelectedOptions.map((selectedOption) => ({
                label: getOptionLabel(selectedOption as SelectOptionType, props.labelKey),
                value: selectedOption[
                    props.valueKey as keyof SelectOptionType
                ] as SelectOptionValue,
            }));
        },
        { immediate: true }
    );

    const clearSearch = () => {
        if (!searchRef.value || !search.value.length) {
            return;
        }

        setTimeout(() => {
            search.value = '';
        }, 200);
    };

    const shouldCloseOnSelect = computed(() => {
        if (props.closeOnSelect !== undefined) {
            return props.closeOnSelect;
        }
        return !props.multiple;
    });

    const closeAndFocus = (isEnter: boolean) => {
        clearSearch();
        close();
        if (props.focusOnEnter || !isEnter) {
            focusTrigger();
        }
    };

    const handleSelectAll = () => {
        if (isAllSelected.value) {
            return [];
        }
        return allValues.value.slice(0, props.limit) as string[];
    };

    const toggleMultipleSelection = (value: SelectOptionValue): string[] => {
        const currentValues = [...((formattedValue.value as string[]) || [])];

        if (currentValues.includes(value as string)) {
            return currentValues.filter((v) => v !== value);
        }
        if (currentValues.length < props.limit) {
            return [...currentValues, value as string];
        }
        return currentValues;
    };

    const emitUpdate = (newValue: ValueType | null) => {
        if (props.selected === undefined) {
            return;
        }

        if (props.multiple) {
            const resolved = resolveMultipleSelection((newValue as string[]) || []);
            emit('update', resolved);
        } else {
            const resolved = resolveSingleSelection(newValue as SelectOptionValue);
            emit('update', resolved);
        }
    };

    const onSelect = (value: SelectOptionValue, element: HTMLElement, isEnter: boolean) => {
        optionListRef.value?.focusElement(element);

        if (!props.multiple) {
            emitValue(value);
            emitUpdate(value);
            if (props.resetAfter) {
                emitValue(null);
                emitUpdate(null);
            }
            closeAndFocus(isEnter);
            return;
        }

        const newValues =
            value === SELECT_ALL_VALUE ? handleSelectAll() : toggleMultipleSelection(value);

        emitValue(newValues);
        emitUpdate(newValues);

        if (props.resetAfter) {
            emitValue([]);
            emitUpdate([]);
        }

        if (value !== SELECT_ALL_VALUE && shouldCloseOnSelect.value) {
            closeAndFocus(isEnter);
        }
    };

    const onDelete = (value: SelectOptionValue) => {
        if (!props.multiple) {
            return;
        }

        const currentValues = [...((formattedValue.value as string[]) || [])];
        const newValues = currentValues.filter((currentValue) => currentValue !== value);

        emitValue(newValues);
        emitUpdate(newValues);
    };

    const onClear = () => {
        if (!props.clearable || props.disabled) {
            return;
        }

        const clearedValue = props.multiple ? [] : null;
        emitValue(clearedValue);
        emitUpdate(clearedValue);
    };

    const onKeyTab = () => {
        if (!searchRef.value) {
            return;
        }

        if (searchRef.value.isFocused()) {
            optionListRef.value?.focus();
        } else {
            searchRef.value.focus();
        }
    };

    const onKeyDown = () => {
        if (!searchRef.value) {
            return;
        }

        if (searchRef.value.isFocused()) {
            optionListRef.value?.focus();
        }
    };

    const isOptionSelectable = (option: SelectOptionType): boolean => {
        return !isOptionDisabled(option) && !isOptionDisabledByLimit(option);
    };

    const getFirstFilteredSelectableOption = (): SelectOptionType | undefined => {
        if (isGrouped.value) {
            for (const group of filteredGroupedOptions.value) {
                const selectableOption = group.options.find(isOptionSelectable);

                if (selectableOption) {
                    return selectableOption;
                }
            }

            return undefined;
        }

        return filteredFlatOptions.value.find(isOptionSelectable);
    };

    const onKeyEnter = (event: KeyboardEvent) => {
        if (!searchRef.value) {
            return;
        }

        if (!searchRef.value.isFocused()) {
            return;
        }

        const availableOptions = optionsRef.value.filter((option) => !option.disabled);
        const focusedSelectedOption = availableOptions.find(
            (option) => option.isSelected && option.isFocused
        );

        if (focusedSelectedOption) {
            focusedSelectedOption.onSelect(event);
            return;
        }

        const firstOption = getFirstFilteredSelectableOption();

        if (firstOption) {
            const firstOptionValue = getOptionValue(firstOption, props.valueKey);
            const renderedOption = availableOptions.find(
                (option) => option.value === firstOptionValue
            );

            if (renderedOption) {
                renderedOption.onSelect(event);
                return;
            }

            const firstOptionElement = optionListRef.value
                ?.options()
                .find((element) => element.dataset.value === String(firstOptionValue));

            if (firstOptionElement) {
                onSelect(firstOptionValue, firstOptionElement, true);
                return;
            }
        }

        if (filteredOptions.value.length) {
            const fallbackFirstOption = filteredOptions.value[0] as SelectOptionType;
            const fallbackRenderedOption = availableOptions.find(
                (option) => option.value === fallbackFirstOption.value
            );

            if (fallbackRenderedOption) {
                fallbackRenderedOption.onSelect(event);
                return;
            }
        }

        if (!props.multiple || !props.taggable) {
            return;
        }

        const nextTag = search.value.trim();

        if (!nextTag.length) {
            return;
        }

        const currentValues = [...((formattedValue.value as string[]) || [])];

        if (!currentValues.includes(nextTag) && currentValues.length < props.limit) {
            const newValues = [...currentValues, nextTag];

            emitValue(newValues);
            emitUpdate(newValues);
        }

        clearSearch();
    };

    const onKeyEscape = () => {
        clearSearch();
        close();
        focusTrigger();
    };

    const onOpen = () => {
        if (searchRef.value) {
            searchRef.value.focus();
        } else if (optionListRef.value) {
            optionListRef.value.focus();
        }
    };

    const focusTrigger = () => {
        const button = triggerRef.value?.querySelector('button');

        if (!button) {
            debugLog('[ActivixSelect] The "trigger" slot must contain a <button> element.');
            return;
        }

        button.focus();
    };

    const blur = () => {
        if (wrapperRef.value?.contains(document.activeElement)) {
            (document.activeElement as HTMLElement)?.blur();
        }
    };

    const open = () => {
        show.value = true;
        initiallyOpened.value = true;
        popperRef.value?.open();
        emit('open');
    };

    const toggle = () => {
        if (props.disabled) {
            return;
        }

        if (show.value) {
            close();
        } else {
            open();
        }
    };

    const close = () => {
        if (!show.value) {
            return;
        }

        show.value = false;
        clearSearch();
        emit('blur');
        popperRef.value?.close();
        emit('close');
    };

    const refs = reactive<SelectRefs>({ trigger: null });

    provide<SelectState>('state', state);
    provide<SelectEvents>('events', { onSelect, onDelete, onClear });
    provide<SelectRefs>('refs', refs);

    const focus = () => {
        focusTrigger();
    };

    onMounted(() => {
        refs.trigger = triggerRef.value;

        if (props.automaticDropdown) {
            open();
        } else if (props.automaticFocus) {
            focusTrigger();
        }
    });

    defineExpose({
        show,
        search,
        initiallyOpened,
        hasHovered,
        shouldRenderOptions,
        onKeyDown,
        onKeyEnter,
        onKeyEscape,
        onKeyTab,
        open,
        close,
        toggle,
        blur,
        focus,
        clearSearch,
    });
</script>
